import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Calendar, MapPin, Clock, User, Phone, Mail,
  Plus, Check, AlertTriangle, X, ChevronLeft, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';

const DateCheckinPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeCheckins, setActiveCheckins] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    match_name: '',
    date_location: '',
    scheduled_time: '',
    checkin_time: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_email: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [activeRes, contactRes] = await Promise.all([
        api.get('/date-checkin/active'),
        api.get('/date-checkin/emergency-contact')
      ]);
      setActiveCheckins(activeRes.data.checkins || []);
      
      const contact = contactRes.data.emergency_contact;
      if (contact) {
        setFormData(prev => ({
          ...prev,
          emergency_contact_name: contact.name || '',
          emergency_contact_phone: contact.phone || '',
          emergency_contact_email: contact.email || '',
        }));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!formData.match_name.trim()) {
      toast.error("Please enter who you're meeting");
      return;
    }
    if (!formData.date_location.trim()) {
      toast.error('Please enter the location');
      return;
    }
    if (!formData.emergency_contact_name.trim()) {
      toast.error('Please add an emergency contact');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/date-checkin/schedule', formData);
      toast.success("Date check-in scheduled! We'll check on you during your date.");
      setShowScheduleModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResponse = async (status) => {
    if (!selectedCheckin) return;
    
    setSubmitting(true);
    try {
      const response = await api.post(`/date-checkin/${selectedCheckin.id}/respond`, { status });
      toast.success(response.data.message);
      setShowResponseModal(false);
      setSelectedCheckin(null);
      loadData();
    } catch (error) {
      toast.error('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(prev => ({
      ...prev,
      match_name: '',
      date_location: '',
      scheduled_time: '',
      checkin_time: '',
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1C3A7A] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-[#1C3A7A] text-white py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => window.history.back()}
            className="flex items-center text-white/80 hover:text-white mb-4"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Date Check-In</h1>
              <p className="text-white/80">Stay safe on your dates</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-6">
        {/* Info Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#1C3A7A]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-[#1C3A7A]" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 mb-1">How it works</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Schedule a check-in before your date. We'll send you a notification at the scheduled time 
                to make sure you're okay. If you don't respond within 30 minutes, we'll alert your emergency contact.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Schedule Button */}
        <Button
          onClick={() => setShowScheduleModal(true)}
          className="w-full h-14 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl flex items-center justify-center gap-2 mb-6"
        >
          <Plus className="w-5 h-5" />
          Schedule Date Check-In
        </Button>

        {/* Active Check-ins */}
        {activeCheckins.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Active Check-Ins</h3>
            <div className="space-y-3">
              {activeCheckins.map((checkin) => (
                <motion.div
                  key={checkin.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setSelectedCheckin(checkin);
                    setShowResponseModal(true);
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[#FAB710]" />
                    <span className="font-medium text-gray-900">{checkin.match_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span>{checkin.date_location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Check-in: {new Date(checkin.checkin_time).toLocaleString()}</span>
                  </div>
                  <div className="mt-3">
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                      checkin.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                      checkin.status === 'pending_response' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {checkin.status.replace('_', ' ')}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {activeCheckins.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No active check-ins</p>
            <p className="text-gray-400 text-sm">Schedule one before your next date!</p>
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      <AnimatePresence>
        {showScheduleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowScheduleModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">Schedule Check-In</h2>
                  <button onClick={() => setShowScheduleModal(false)}>
                    <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSchedule} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Who are you meeting?
                  </label>
                  <input
                    type="text"
                    value={formData.match_name}
                    onChange={e => setFormData(p => ({ ...p, match_name: e.target.value }))}
                    placeholder="Their name"
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Where?
                  </label>
                  <input
                    type="text"
                    value={formData.date_location}
                    onChange={e => setFormData(p => ({ ...p, date_location: e.target.value }))}
                    placeholder="Restaurant, coffee shop, etc."
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={e => setFormData(p => ({ ...p, scheduled_time: e.target.value }))}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    When should we check on you?
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.checkin_time}
                    onChange={e => setFormData(p => ({ ...p, checkin_time: e.target.value }))}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                  />
                </div>

                <div className="bg-[#FAB710]/10 rounded-xl p-4 mt-6">
                  <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#FAB710]" />
                    Emergency Contact
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We'll contact them if you don't respond to the check-in
                  </p>

                  <div className="space-y-3">
                    <input
                      type="text"
                      value={formData.emergency_contact_name}
                      onChange={e => setFormData(p => ({ ...p, emergency_contact_name: e.target.value }))}
                      placeholder="Contact name"
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                    />
                    <input
                      type="tel"
                      value={formData.emergency_contact_phone}
                      onChange={e => setFormData(p => ({ ...p, emergency_contact_phone: e.target.value }))}
                      placeholder="Phone number"
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                    />
                    <input
                      type="email"
                      value={formData.emergency_contact_email}
                      onChange={e => setFormData(p => ({ ...p, emergency_contact_email: e.target.value }))}
                      placeholder="Email address"
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl mt-6"
                >
                  {submitting ? 'Scheduling...' : 'Schedule Check-In'}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Response Modal */}
      <AnimatePresence>
        {showResponseModal && selectedCheckin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50"
            onClick={() => setShowResponseModal(false)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md p-6"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-center text-gray-900 mb-1">
                How's your date going?
              </h2>
              <p className="text-gray-500 text-center mb-6">
                Meeting with {selectedCheckin.match_name}
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => handleResponse('safe')}
                  disabled={submitting}
                  className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Check className="w-5 h-5" />
                  I'm Safe! 👍
                </button>

                <button
                  onClick={() => handleResponse('ended')}
                  disabled={submitting}
                  className="w-full py-4 bg-[#1C3A7A] hover:bg-[#15305f] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Date Ended
                </button>

                <button
                  onClick={() => handleResponse('need_help')}
                  disabled={submitting}
                  className="w-full py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <AlertTriangle className="w-5 h-5" />
                  I Need Help! 🚨
                </button>

                <button
                  onClick={() => setShowResponseModal(false)}
                  className="w-full py-3 text-gray-500 font-medium"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DateCheckinPage;
